import { html } from '@effectivastudio/lit-components';
import BaseMenu from '@effectivastudio/lit-components/components/base_menu/element.js';
import '@effectivastudio/lit-components/components/dot_menu/element.js';
import translations from './translations.js';

/**
 * Dot dot menu for group members
 *
 * @prop resourceName - Member name
 * @prop groupId - Id of the group
 * @prop memberId - Id of the group member
 * @prop setContactPersonOn - Should show set as contact person option in menu?
 * @prop blockOn - Should show block option in menu?
 * @prop unblockOn - Should show unblock option in menu?
 * @prop editRightsOn - Should show edit rights option in menu?
 * @prop removeOn - Should show remove option in menu?
 * @prop setContactPersonValue - Is this member a contact person of the group?
 *
 */

export default class MemberGroupMenu extends BaseMenu {
  static properties = {
    resourceName: { type: String },
    memberId: { type: Number },
    groupId: { type: Number },
    setContactPersonValue: { type: Boolean },
    setContactPersonOn: { type: Boolean },
    unblockOn: { type: Boolean },
    blockOn: { type: Boolean },
    editRightsOn: { type: Boolean },
    removeOn: { type: Boolean },
  };

  /** @internal */
  static translations = translations;

  /** @internal */
  get instanceId() {
    return `${this.groupId}-${this.memberId}`;
  }

  /** @internal */
  get baseUrl() {
    return `/groups/${this.groupId}/group_members/${this.memberId}`;
  }

  /** @internal */
  get setContactPersonUrl() {
    return `${this.baseUrl}/toggle_contact_person`;
  }

  /** @internal */
  get unblockUrl() {
    return `${this.baseUrl}/unblock`;
  }

  /** @internal */
  get blockUrl() {
    return `${this.baseUrl}/block`;
  }

  /** @internal */
  get editRightsUrl() {
    return `${this.baseUrl}/edit`;
  }

  /** @internal */
  get removeUrl() {
    return `${this.baseUrl}/remove`;
  }

  /** @internal */
  get setContactPersonItem() {
    const isContactString = this.setContactPersonValue ? 'on' : 'off';
    return {
      title: this.t(`actions.set_as_contact_person.${isContactString}`),
      action: this.actionFor('memberPatch', [
        this.setContactPersonUrl,
        `set_as_contact_person.${isContactString}`,
      ]),
      icon: this.setContactPersonValue ? 'user-headset:solid' : 'user-headset',
      cssClass: 'set-contact-person',
    };
  }

  /** @internal */
  get blockItem() {
    return {
      title: this.t('actions.block'),
      action: this.actionFor('memberBlock', [this.blockUrl, 'block']),
      icon: 'ban',
      cssClass: 'block',
    };
  }

  /** @internal */
  get unblockItem() {
    return {
      title: this.t('actions.unblock'),
      action: this.actionFor('memberPatch', [this.unblockUrl, 'unblock']),
      icon: 'ban:solid',
      cssClass: 'unblock',
    };
  }

  /** @internal */
  get editRightsItem() {
    return {
      title: this.t('actions.edit_rights'),
      action: this.actionFor('memberExecJSFromResponse', [this.editRightsUrl]),
      icon: 'pencil',
      cssClass: 'edit-rights',
    };
  }

  /** @internal */
  get removeItem() {
    return {
      title: this.t(`actions.remove`),
      action: this.actionFor('memberRemove'),
      role: 'destructive',
      icon: 'close',
      cssClass: 'remove text-danger',
    };
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get dividerOn() {
    return (
      (this.setContactPersonOn ||
        this.editRightsOn ||
        this.blockOn ||
        this.unblockOn) &&
      this.removeOn
    );
  }

  /** @internal */
  get items() {
    return [
      this.setContactPersonOn && this.setContactPersonItem,
      this.blockOn && this.blockItem,
      this.unblockOn && this.unblockItem,
      this.editRightsOn && this.editRightsItem,
      this.dividerOn && this.divider,
      this.removeOn && this.removeItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      header: this.resourceName,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'xs-member-group-menu',
      id: this.instanceId,
      method: method,
      ...(args && { args: args }),
    };
  }

  memberExecJSFromResponse(url) {
    this.commonActions.execJSFromResponse(url);
  }

  async memberPatch(url, action) {
    try {
      await this.commonActions.patch(url);
      this.dispatchEvent(
        new Event('member-updated', { bubbles: true, composed: true })
      );
      this.commonActions.displaySnackBarSuccess(
        this.t(`snack.${action}_success`)
      );
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  async memberBlock() {
    if (!this.commonActions.confirmDialog(this.t('confirm_dialog_msg.default')))
      return false;
    await this.memberPatch(this.blockUrl, 'block');
  }

  async memberRemove() {
    if (!this.commonActions.confirmDialog(this.t('confirm_dialog_msg.default')))
      return false;
    try {
      await this.commonActions.post(this.removeUrl);
      this.dispatchEvent(
        new Event('member-removed', { bubbles: true, composed: true })
      );
      this.commonActions.displaySnackBarSuccess(this.t(`snack.remove_success`));
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  render() {
    return html`<ef-dot-menu .config=${this.dotMenuConfig}></ef-dot-menu>`;
  }
}

MemberGroupMenu.registerTagName('xs-member-group-menu');
MemberGroupMenu.registerCommonActions('xs-member-group-menu');

customElements.get('xs-member-group-menu') ||
  customElements.define('xs-member-group-menu', MemberGroupMenu);
